/**
 * Replacer для JSON.stringify: сортирует массивы для корректного сравнения объектов
 * @param key - ключ свойства
 * @param value - значение свойства
 * @returns значение для сериализации
 */
export const jsonStringifyReplacer = (key: string, value: unknown): unknown => {
  if (Array.isArray(value)) {
    return [...value].sort((a, b) => {
      const strA = JSON.stringify(a);
      const strB = JSON.stringify(b);
      return strA.localeCompare(strB);
    });
  }
  return value;
};

/**
 * Сравнивает два объекта с учетом того, что порядок элементов в массивах не важен
 * @param obj1 - первый объект для сравнения
 * @param obj2 - второй объект для сравнения
 * @returns true если объекты равны, false в противном случае
 */
export const deepEqual = (obj1: unknown, obj2: unknown): boolean => {
  return JSON.stringify(obj1, jsonStringifyReplacer) === JSON.stringify(obj2, jsonStringifyReplacer);
};

/**
 * Type guard: проверяет, что ключ является собственным ключом объекта.
 * @param key - проверяемый ключ
 * @param object - объект-эталон
 * @returns true, если key — собственный ключ object (сужает тип до keyof T)
 */
export const isObjectKey = <T extends object>(key: PropertyKey, object: T): key is keyof T =>
  typeof key === 'string' && Object.hasOwn(object, key);

/**
 * Оставляет из source только те ключи, которые есть в reference.
 * Полезно для отбрасывания устаревших/неизвестных полей (например, из localStorage).
 * @param source - объект, из которого берём значения
 * @param reference - эталон допустимых ключей
 * @returns объект только с известными ключами
 */
export const pickKnownKeys = <T extends Record<string, unknown>>(
  source: Record<string, unknown>,
  reference: T,
): Partial<T> => {
  return Object.fromEntries(Object.entries(source).filter(([key]) => key in reference)) as Partial<T>;
};
